// Shared geometry + helpers for the free-form sprite layer system.
// The card template (template.png) is an opaque card body with a transparent
// die-cut "art window" — only that window shows the background image + sprites,
// everything outside it is covered by the printed card frame/text area. So the
// sprite layer's logical coordinate space is the ART WINDOW itself (289 x 181,
// matched to .card-art-layer's CSS box in PokemonCard.css), not the full 350x500
// card. Pointer math converts through the live bounding rect of .card-art-layer,
// so it stays correct at any CSS zoom level used for responsive scaling.

export const CARD_W = 289;
export const CARD_H = 181;

// Background images (public/backgrounds/*) are pre-composited by the artist at
// the SAME 750x1050 canvas as template.png, with the artwork already drawn to
// line up with the die-cut window — everywhere else in the file is transparent
// padding. So .card-bg-img must render at the full card's native scale (fill,
// matching how .card-template-overlay is scaled) and simply get clipped by the
// window box, NOT be resized/cropped to fit the small window box directly (that
// would re-crop an arbitrary slice of the image instead of showing the part the
// artist actually placed there). These must stay in sync with PokemonCard.css's
// .card-art-layer (left/top) and .pokemon-card (width/height).
export const FULL_CARD_W = 350;
export const FULL_CARD_H = 500;
export const ART_WINDOW_LEFT = 31;
export const ART_WINDOW_TOP = 54;

// Non-watermarked card frame (same "heart type" badge as the live template.png
// overlay, just without the tiled watermark) — used only when generating the
// order PNG/PSD, never for the on-screen preview (which keeps template.png).
export const CARD_FRAME_FULL_SRC = '/Backgrounds%20full/01%20Frame%20Background%20with%20Heart%20Type.png';

// Fixed sprite footprints (no resize feature — see DraggableSprite.js).
// object-fit: contain on the <img> keeps each sprite's own aspect ratio inside it.
export const DEFAULT_SIZE = {
  trainer: { w: 40, h: 40 },
  pokemon: { w: 32, h: 32 },
};

let uidCounter = 0;
export const makeId = () => `s${Date.now().toString(36)}${(uidCounter++).toString(36)}`;

export const spriteSrc = (kind, name, POKEMON_SPRITES) => {
  if (kind === 'trainer') return `/trainers/${name}.png`;
  return POKEMON_SPRITES.includes(name) ? `/Pokemons%20individual/${name}.png` : '/question_mark3.png';
};

// Build a new sprite instance.
export const createSprite = (kind, name, { x, y, z, details, w, h } = {}) => ({
  id: makeId(),
  kind,          // 'trainer' | 'pokemon'
  name,          // sprite key, e.g. 'Ash' or 'Pikachu' (or free text for custom pokemon)
  x: x ?? CARD_W / 2,
  y: y ?? CARD_H / 2,
  w: w ?? DEFAULT_SIZE[kind].w,
  h: h ?? DEFAULT_SIZE[kind].h,
  flip: false,
  z: z ?? 1,
  details: details ?? '',
});

export const nextZ = (sprites) => sprites.reduce((m, s) => Math.max(m, s.z), 0) + 1;

export const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// Convert a pointer event's client coords into logical card-space coords,
// accounting for the card's current on-screen scale (e.g. mobile CSS zoom).
export const toCardPoint = (cardEl, clientX, clientY) => {
  const rect = cardEl.getBoundingClientRect();
  const scaleX = rect.width / CARD_W;
  const scaleY = rect.height / CARD_H;
  return {
    x: (clientX - rect.left) / scaleX,
    y: (clientY - rect.top) / scaleY,
    scaleX, scaleY,
  };
};

export const fmtName = (name) =>
  (name || '').replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
